"use client"

import styled from "@emotion/styled"
import { Article, GetArticlesQuery } from "@eurodrive/apollo"
import Container from "@eurodrive/ui/Container"
import { sharedColors } from "@eurodrive/ui/theme/colors"
import { getNonUmbracoUrl } from "@eurodrive/utils"
import { getUmbracoMediaUrl } from "@eurodrive/utils/getUmbracoMediaUrl"
import { Typography } from "@mui/material"
import Image from "next/image"
import Link from "next/link"

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  padding-bottom: 64px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const StyledItemLayout = styled.div`
  width: 100%;
  height: 380px;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  box-sizing: border-box;
  border: 1px solid #ece9e9;
  background-color: white;
  overflow: hidden;
`

const ImageContainer = styled.div`
  width: 100%;
  height: 160px;
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
  :hover {
    img {
      transform: scale(1.2);
    }
  }

  img {
    transition: transform 0.3s ease-in-out;
  }
`

const Content = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`

const CategoryChip = styled.div`
  display: flex;
  padding: 2px 6px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  width: fit-content;
  background-color: ${sharedColors.lightGrey};
`

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledLink = styled(Link)`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: auto;
  text-decoration: none;
  width: fit-content;
  :hover {
    /* only bottom border */
    box-shadow: inset 0px -1px 0px ${sharedColors.darkGreen};
  }
`

const StyledDescription = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledTitle = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

export default function ArticlesGrid({
  articles,
}: {
  articles: GetArticlesQuery
}) {
  const articlesArray =
    articles.components?.edges?.map((el) => {
      const object = el.node
      const properties = object?.namedProperties as Article
      return {
        url: object?.url,
        title: properties?.title?.value,
        image: properties?.imageThumbnailForLobbyPage?.mediaItems?.[0]?.url,
        description: properties?.description?.value,
        category: properties?.category?.value,
      }
    }) || []
  return (
    <Container paddingMobile>
      <StyledGrid>
        {articlesArray.map((item, i) => (
          <StyledItemLayout key={i}>
            <ImageContainer>
              {item.image && (
                <Image
                  fill
                  src={getUmbracoMediaUrl(item.image || "")}
                  alt={item.title}
                  style={{ objectFit: "cover" }}
                />
              )}
            </ImageContainer>
            <Content>
              <CategoryChip>
                <Typography variant="body2">{item.category}</Typography>
              </CategoryChip>
              <TextBox>
                <StyledTitle variant="h3">{item.title}</StyledTitle>
                <StyledDescription variant="body1">
                  {item.description}
                </StyledDescription>
              </TextBox>
              <StyledLink href={getNonUmbracoUrl(item.url || "")} title={item.title}>
                <Typography
                  variant="body1"
                  color={sharedColors.darkGreen}
                  fontWeight={600}
                >
                  לקריאת הכתבה
                </Typography>
                <Image
                  src={"/icons/left-arrow-alt-green.svg"}
                  alt=""
                  aria-hidden="true"
                  width={24}
                  height={24}
                />
              </StyledLink>
            </Content>
          </StyledItemLayout>
        ))}
      </StyledGrid>
    </Container>
  )
}
